import React, { useState, memo } from 'react';
import axios from 'axios';
import { Table, Container, Spinner, Button } from 'react-bootstrap';
import apiUrl from '../const';
import { connect, useDispatch } from 'react-redux';
import { salvaDadosCliente } from '../redux/actions';

// Função para formatar o tamanho dos arquivos (já está em bytes)
const formatFileSize = (size) => {
    if (size === undefined || size === null || size < 1000) {
        return '0 KB';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
};

// Função para formatar o tamanho do plano (vem em KB do banco, converte para bytes antes de formatar)
const formatPlanoSize = (sizeKB) => {
    if (sizeKB === undefined || sizeKB === null || sizeKB <= 0) {
        return '0 KB';
    }
    const sizeBytes = sizeKB * 1000; // Converte KB para bytes
    return formatFileSize(sizeBytes);
};

// Função para calcular a porcentagem de uso usando o tamanho do plano do backend
// Nota: planoTamanho vem do banco em KB, então multiplicamos por 1000 para converter para bytes
const calcularPorcentagem = (usado, planoTamanhoKB) => {
    if (!planoTamanhoKB || planoTamanhoKB === 0) return 0;
    const planoTamanhoBytes = planoTamanhoKB * 1000;
    return ((usado / planoTamanhoBytes) * 100).toFixed(1);
};

const UsoDriveClientes = (props) => {
    const dispatch = useDispatch();
    const [clientes, setClientes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [ordenacao, setOrdenacao] = useState({ campo: 'nome', direcao: 'asc' });

    // Temas para estilos
    const tableTemaEscuro = 'table-dark table-striped table-bordered table-hover';
    const tableTemaClaro = 'table-light table-striped table-bordered table-hover';

    // Função para abrir o painel do cliente
    const handleAbrirPainel = (cliente) => {
        axios.post(`${apiUrl}/login`, {
            emailInput: cliente.usuario,
            senhaInput: "null",
            senhaSemCripto: cliente.senha
        }).then((response) => {
            localStorage.setItem('token', response.data.token);
            dispatch(salvaDadosCliente(response.data));
            window.open('https://upcloud.net.br/painel/', '_blank');
        }).catch((e) => {
            alert('Erro ao abrir painel do cliente');
            console.error(e);
        });
    };

    // Função para buscar dados do uso do Drive
    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${apiUrl}/getUsoDriveClientes`, {
                timeout: 300000 // 5 minutos de timeout
            });
            setClientes(response.data.clientes || []);
            setIsLoading(false);
        } catch (error) {
            console.error('Erro ao buscar uso do Drive:', error);
            alert('Erro ao buscar dados de uso do Drive. Verifique o console para mais detalhes.');
            setIsLoading(false);
        }
    };

    // Função para ordenar os clientes
    const ordenarClientes = (campo) => {
        const novaDirecao = ordenacao.campo === campo && ordenacao.direcao === 'asc' ? 'desc' : 'asc';
        setOrdenacao({ campo, direcao: novaDirecao });

        const clientesOrdenados = [...clientes].sort((a, b) => {
            let valorA, valorB;

            // Para ordenar por progresso, calculamos a porcentagem
            if (campo === 'progresso') {
                valorA = a.planoTamanho > 0 ? (a.totalUsadoDrive / (a.planoTamanho * 1000)) * 100 : 0;
                valorB = b.planoTamanho > 0 ? (b.totalUsadoDrive / (b.planoTamanho * 1000)) * 100 : 0;
            } else {
                valorA = a[campo];
                valorB = b[campo];
            }

            if (campo === 'totalUsadoDrive' || campo === 'planoTamanho') {
                valorA = valorA || 0;
                valorB = valorB || 0;
            }

            if (typeof valorA === 'string') {
                valorA = valorA.toLowerCase();
                valorB = valorB.toLowerCase();
            }

            if (novaDirecao === 'asc') {
                return valorA > valorB ? 1 : -1;
            }
            return valorA < valorB ? 1 : -1;
        });

        setClientes(clientesOrdenados);
    };

    // Ícone de ordenação
    const IconeOrdenacao = ({ campo }) => {
        if (ordenacao.campo !== campo) return <span className="ml-1">⇅</span>;
        return ordenacao.direcao === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
    };

    // Cor da barra de progresso baseada na porcentagem
    const getCorBarra = (porcentagem) => {
        if (porcentagem >= 90) return 'bg-danger';
        if (porcentagem >= 70) return 'bg-warning';
        return 'bg-success';
    };


    // Renderização condicional baseada no estado de carregamento e dados
    if (isLoading) {
        return (
            <Container className="text-center my-5 border p-5 rounded-lg">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
                <p className="mt-3">Carregando dados de uso do Google Drive...</p>
                <p className="text-muted">Isso pode demorar alguns minutos dependendo do número de clientes.</p>
            </Container>
        );
    }

    if (clientes.length === 0) {
        return (
            <Container className="text-center my-5 border p-5 rounded-lg">
                <h4 className="mb-4">Uso do Google Drive por Cliente</h4>
                <p className="text-muted mb-4">
                    Clique no botão abaixo para carregar o uso do Drive de todos os clientes.
                </p>
                <Button
                    className={props.tema === 'escuro' ? '' : 'text-black'}
                    onClick={fetchData}
                    variant="primary"
                    size="lg"
                >
                    <i className="bi bi-cloud-download me-2"></i>
                    Carregar Uso do Drive
                </Button>
            </Container>
        );
    }

    // Calcular totais
    const totalGeral = clientes.reduce((acc, c) => acc + (c.totalUsadoDrive || 0), 0);

    // Calcular estatísticas por faixa de uso
    const estatisticas = clientes.reduce((acc, cliente) => {
        const porcentagem = cliente.planoTamanho > 0 
            ? (cliente.totalUsadoDrive / (cliente.planoTamanho * 1000)) * 100 
            : 0;
        
        if (porcentagem >= 100) {
            acc.acima100++;
        } else if (porcentagem >= 75) {
            acc.entre75e100++;
        } else if (porcentagem >= 50) {
            acc.entre50e75++;
        } else {
            acc.abaixo50++;
        }
        return acc;
    }, { acima100: 0, entre75e100: 0, entre50e75: 0, abaixo50: 0 });

    return (
        <Container className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <h4>Uso do Google Drive por Cliente</h4>
                    <p style={{ opacity: 0.7 }} className="mb-1">
                        Total de clientes: {clientes.length} | 
                        Uso total: {formatFileSize(totalGeral)}
                    </p>
                    <div className="d-flex gap-3 flex-wrap">
                        <span className="badge border border-danger" style={{ fontSize: '0.85rem' }}>
                            🔴 Acima de 100%: {estatisticas.acima100}
                        </span>
                        <span className="badge border border-warning" style={{ fontSize: '0.85rem' }}>
                            🟡 75% - 100%: {estatisticas.entre75e100}
                        </span>
                        <span className="badge border border-info" style={{ fontSize: '0.85rem' }}>
                            🔵 50% - 75%: {estatisticas.entre50e75}
                        </span>
                        <span className="badge border border-success" style={{ fontSize: '0.85rem' }}>
                            🟢 Abaixo de 50%: {estatisticas.abaixo50}
                        </span>
                    </div>
                </div>
                <Button
                    className={props.tema === 'escuro' ? '' : 'text-black'}
                    onClick={fetchData}
                    variant="outline-primary"
                >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Atualizar
                </Button>
            </div>

            <Table
                bordered
                responsive
                className={`${props.tema === 'claro' ? tableTemaClaro : tableTemaEscuro} text-center`}
            >
                <thead>
                    <tr>
                        <th 
                            className="cursor-pointer" 
                            onClick={() => ordenarClientes('nome')}
                            style={{ cursor: 'pointer' }}
                        >
                            Cliente <IconeOrdenacao campo="nome" />
                        </th>
                        <th 
                            className="cursor-pointer" 
                            onClick={() => ordenarClientes('inscricao')}
                            style={{ cursor: 'pointer' }}
                        >
                            Inscrição <IconeOrdenacao campo="inscricao" />
                        </th>
                        <th 
                            className="cursor-pointer" 
                            onClick={() => ordenarClientes('planoDescricao')}
                            style={{ cursor: 'pointer' }}
                        >
                            Plano <IconeOrdenacao campo="planoDescricao" />
                        </th>
                        <th 
                            className="cursor-pointer" 
                            onClick={() => ordenarClientes('totalUsadoDrive')}
                            style={{ cursor: 'pointer' }}
                        >
                            Uso no Drive <IconeOrdenacao campo="totalUsadoDrive" />
                        </th>
                        <th 
                            className="cursor-pointer" 
                            onClick={() => ordenarClientes('progresso')}
                            style={{ cursor: 'pointer' }}
                        >
                            Progresso <IconeOrdenacao campo="progresso" />
                        </th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {clientes.map((cliente, index) => {
                        const porcentagem = calcularPorcentagem(cliente.totalUsadoDrive, cliente.planoTamanho);
                        
                        return (
                            <tr key={index}>
                                <td className="text-start">
                                    <span 
                                        className="cursor-pointer hover:underline"
                                        onClick={() => navigator.clipboard.writeText(cliente.nome)}
                                        style={{ cursor: 'pointer' }}
                                        title="Clique para copiar"
                                    >
                                        {cliente.nome}
                                    </span>
                                </td>
                                <td>
                                    <span 
                                        className="cursor-pointer hover:underline"
                                        onClick={() => navigator.clipboard.writeText(cliente.inscricao)}
                                        style={{ cursor: 'pointer' }}
                                        title="Clique para copiar"
                                    >
                                        {cliente.inscricao}
                                    </span>
                                </td>
                                <td>
                                    <span className="badge bg-secondary">
                                        {cliente.planoDescricao || 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    <strong>{formatFileSize(cliente.totalUsadoDrive)}</strong>
                                    {cliente.planoTamanho > 0 && (
                                        <small className="d-block" style={{ opacity: 0.7 }}>
                                            de {formatPlanoSize(cliente.planoTamanho)}
                                        </small>
                                    )}
                                </td>
                                <td style={{ minWidth: '200px' }}>
                                    {cliente.planoTamanho > 0 ? (
                                        <div className="progress" style={{ height: '20px', backgroundColor: props.tema === 'escuro' ? '#333' : '#e9ecef' }}>
                                            <div 
                                                className={`progress-bar ${getCorBarra(porcentagem)}`}
                                                role="progressbar"
                                                style={{ width: `${Math.min(porcentagem, 100)}%` }}
                                                aria-valuenow={porcentagem}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            >
                                                <span style={{ color: 'white', fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                                                    {porcentagem}%
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="badge bg-info">Sem limite</span>
                                    )}
                                </td>
                                <td>
                                    <Button 
                                        variant="primary" 
                                        size="sm"
                                        onClick={() => handleAbrirPainel(cliente)}
                                        className={`${props.tema === 'claro' && 'text-black hover:bg-blue-400'}`}
                                    >
                                        Abrir Painel
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </Container>
    );
};

// Mapeamento do estado do Redux para as props do componente
const mapStateToProps = (state) => {
    return {
        tema: state.appReducer.tema,
    };
};

// Exportação do componente com React.memo para otimização
export default connect(mapStateToProps, null)(memo(UsoDriveClientes));
